import type { ComponentProps } from 'react';
import type { ClerkProvider } from '@clerk/nextjs';

/**
 * Unified Clerk Appearance Configuration
 * Uses Tailwind's dark: classes for automatic light/dark mode switching
 *
 * Typed off the ClerkProvider prop rather than `@clerk/types` — the types
 * package is a legacy stub whose `@clerk/shared` pin lags the appearance
 * API the installed components actually use (`options`/`spacing`).
 */
type ClerkAppearance = NonNullable<
  ComponentProps<typeof ClerkProvider>['appearance']
>;

export const clerkAppearance: ClerkAppearance = {
  options: {
    logoImageUrl: '/logo.png',
    logoPlacement: 'inside',
    showOptionalFields: true,
    socialButtonsPlacement: 'bottom',
    socialButtonsVariant: 'iconButton',
    termsPageUrl: '/terms',
    privacyPageUrl: '/privacy',
    helpPageUrl: '/support/help',
  },
  variables: {
    colorPrimary: 'var(--action)',
    colorDanger: '#dc2626', // red-600
    colorSuccess: '#16a34a', // green-600
    colorWarning: '#f59e0b', // Semantic warning, separate from the brand accent.
    borderRadius: '0.5rem',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: '14px',
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    spacing: '1rem',
  },
  elements: {
    // Root container
    rootBox: 'w-full max-w-md mx-auto',

    // Card - uses dark: for automatic theme switching
    card: 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800/40 rounded-xl shadow-lg p-8',

    // Header
    headerTitle:
      'text-2xl font-bold text-neutral-900 dark:text-neutral-50 text-center mb-2',
    headerSubtitle:
      'text-sm text-neutral-600 dark:text-neutral-400 text-center mb-6',

    // Form elements
    formFieldInput:
      'w-full px-3 py-2 bg-white dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700/50 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none form-field focus:ring-0 transition-colors',
    formFieldLabel:
      'block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1',
    formFieldErrorText: 'text-sm text-red-600 mt-1',
    formFieldSuccessText: 'text-sm text-green-600 mt-1',
    formFieldWarningText: 'text-sm text-accent-300 mt-1',

    // Buttons
    formButtonPrimary:
      'w-full bg-action hover:bg-action-hover text-action-foreground font-medium py-2 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    formButtonSecondary:
      'w-full bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-900 dark:text-neutral-100 font-medium py-2 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',

    // Social buttons
    socialButtonsBlockButton:
      'w-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700/50 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-900 dark:text-neutral-100 font-medium py-2 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    socialButtonsBlockButtonText: 'text-neutral-900 dark:text-neutral-100',

    // Divider
    dividerLine: 'bg-neutral-300 dark:bg-neutral-700',
    dividerText: 'text-neutral-500 dark:text-neutral-400 text-sm',

    // Footer
    footerActionText: 'text-sm text-neutral-600 dark:text-neutral-400',
    footerActionLink: 'text-primary hover:text-accent-70 font-medium',

    // Identity preview
    identityPreviewText: 'text-neutral-700 dark:text-neutral-300 text-sm',
    identityPreviewEditButton:
      'text-primary hover:text-accent-70 text-sm font-medium',

    // Form resend
    formResendCodeLink: 'text-primary hover:text-accent-70 text-sm font-medium',

    // Alert
    alertText: 'text-sm',
    alertIcon: 'w-5 h-5',

    // Phone input
    phoneInputBox:
      'w-full px-3 py-2 bg-white dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700/50 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none form-field focus:ring-0 transition-colors',

    // Select
    selectButton:
      'w-full px-3 py-2 bg-white dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700/50 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none form-field focus:ring-0 transition-colors',

    // OTP input
    otpCodeFieldInput:
      'w-12 h-12 text-center bg-white dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700/50 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none form-field focus:ring-0 transition-colors',

    // Profile section
    profileSectionTitle:
      'text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4',
    profileSectionContent: 'space-y-4',

    // Avatar
    avatarBox:
      'w-16 h-16 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center',
    avatarImage: 'w-full h-full rounded-full object-cover',

    // File drop area
    fileDropAreaBox:
      'border-2 border-dashed border-neutral-300 dark:border-neutral-700/50 rounded-lg p-6 text-center hover:border-accent-60 transition-colors',
    fileDropAreaBoxDragAccept:
      'border-accent-60 bg-accent-1 dark:bg-accent-700/10',
    fileDropAreaBoxDragReject: 'border-red-500 bg-red-50 dark:bg-red-900/10',
  },
};

/**
 * Auth pages use the shadcn login-03 frame, with Clerk owning every auth step.
 *
 * Clerk appends these classes to the ClerkProvider-level `clerkAppearance`
 * classes rather than replacing them, so `card` ends up carrying both
 * `bg-white p-8 shadow-lg` and `bg-transparent p-0 shadow-none`. Tailwind
 * resolves that by stylesheet order, not class order, so every utility that
 * overrides a provider-level one (or a `dark:` one) uses the `!` modifier.
 *
 * Clerk redefines the border color token on its buttons, so `border-border`
 * resolves to the gold action color inside them. Borders here name a neutral
 * palette color instead.
 */
export const authAppearance: ClerkAppearance = {
  ...clerkAppearance,
  options: {
    ...clerkAppearance.options,
    // Name fields are optional in Clerk; keep sign-up to email + password.
    showOptionalFields: false,
    socialButtonsPlacement: 'top',
    socialButtonsVariant: 'blockButton',
    helpPageUrl: 'https://docs.eclipseaccounting.app/troubleshooting/login',
  },
  elements: {
    ...clerkAppearance.elements,
    rootBox: 'w-full max-w-none',
    cardBox: 'w-full shadow-none! border-0! rounded-none! bg-transparent!',
    card: 'w-full bg-transparent! border-0! shadow-none! p-0! rounded-none! gap-6!',
    logoBox: 'hidden!',
    header: 'gap-2!',
    headerTitle:
      'text-foreground! text-[1.625rem]! leading-tight! font-semibold! tracking-[-0.03em]! text-center text-balance mb-0!',
    headerSubtitle:
      'text-muted-foreground! text-sm! leading-6 text-center text-balance mb-0!',
    main: 'gap-6!',
    form: 'gap-5!',
    formFieldLabel: 'text-foreground! mb-2! text-sm font-medium',
    formFieldAction:
      'text-muted-foreground! hover:text-foreground! text-[13px]! font-medium underline-offset-4 hover:underline',
    formFieldInput:
      'form-field bg-neutral-100/80! text-foreground! placeholder:text-muted-foreground border-transparent! hover:bg-neutral-100! min-h-11 w-full min-w-0 rounded-xl! border px-3.5 py-2 text-base shadow-none! outline-none transition-colors sm:text-sm',
    formButtonPrimary:
      'bg-action text-action-foreground hover:bg-action-hover min-h-11 w-full rounded-xl! px-4 py-2 text-sm font-semibold! shadow-[inset_0_1px_0_rgb(255_255_255/0.4),0_1px_2px_rgb(14_14_16/0.15)]! transition-shadow hover:shadow-[inset_0_1px_0_rgb(255_255_255/0.4),0_10px_28px_-10px_rgb(255_176_0/0.9)]!',
    buttonArrowIcon: 'hidden!',
    socialButtonsBlockButton:
      'bg-white! text-foreground! border-neutral-200! hover:border-neutral-300! hover:bg-neutral-50! min-h-11 w-full rounded-xl! border px-4 py-2 text-sm font-medium shadow-none! transition-colors',
    socialButtonsBlockButtonText: 'text-foreground! text-sm! font-medium',
    socialButtons: 'grid-cols-1 gap-2!',
    dividerRow: 'gap-3!',
    dividerLine: 'bg-neutral-200!',
    dividerText: 'text-muted-foreground! text-xs!',
    identityPreview:
      'bg-neutral-100/80! border-0! shadow-none! rounded-xl! min-h-11 px-3.5!',
    identityPreviewText: 'text-foreground! text-sm',
    identityPreviewEditButton:
      'text-muted-foreground! hover:text-foreground! text-sm font-medium',
    alternativeMethodsBlockButton:
      'bg-white! text-foreground! border-neutral-200! hover:bg-neutral-50! min-h-11 rounded-xl! border shadow-none!',
    badge:
      'bg-accent-5! text-foreground! border-accent-20! rounded-full! border shadow-none!',
    footer: 'bg-transparent! bg-none! mt-5! p-0!',
    footerAction: 'justify-center p-0!',
    footerItem:
      'border-neutral-200/80! mt-5! px-0! pt-4! pb-0! [&_div:has(>.cl-footerPages)]:justify-center!',
    footerActionText: 'text-muted-foreground! text-sm',
    footerActionLink:
      'text-foreground! hover:text-foreground! font-semibold! underline decoration-neutral-300 underline-offset-4 hover:decoration-current',
    // The frame already links Terms, Privacy, and Help below the card.
    footerPages: 'hidden!',
    otpCodeFieldInput:
      'form-field bg-neutral-100/80! text-foreground! border-transparent! size-11! min-w-0 rounded-xl! border text-center text-base shadow-none outline-none',
  },
};

/**
 * UserButton appearance for header (mobile, more compact)
 */
export const headerUserButtonAppearance: ClerkAppearance = {
  elements: {
    userButtonAvatarBox:
      'w-8 h-8 rounded-full border border-neutral-200 dark:border-neutral-800/40',
    userButtonTrigger:
      'focus:shadow-none hover:bg-neutral-100 dark:hover:bg-neutral-900 rounded-full transition-colors',
    userButtonPopoverCard:
      'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800/40 shadow-xl',
    userButtonPopoverActionButton:
      '!text-neutral-700 dark:!text-neutral-300 hover:!bg-neutral-200 hover:!text-neutral-900 dark:hover:!bg-neutral-700 dark:hover:!text-white rounded-md transition-colors',
    userPreviewMainIdentifier:
      '!text-neutral-900 dark:!text-neutral-100 font-semibold',
    userPreviewSecondaryIdentifier: '!text-neutral-600 dark:!text-neutral-400',
  },
};

/**
 * Shared profile appearance for UserProfile and OrganizationProfile
 */
const profileAppearance: ClerkAppearance = {
  variables: {
    colorPrimary: 'var(--action)',
  },
  elements: {
    rootBox: 'w-full',
    card: 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800/40 shadow-none',
    navbar:
      'bg-neutral-50 dark:bg-neutral-950 border-r border-neutral-200 dark:border-neutral-800/40',
    navbarButton:
      '!text-neutral-700 dark:!text-neutral-300 hover:!bg-neutral-100 dark:hover:!bg-neutral-800',
    navbarButtonActive:
      '!text-primary dark:!text-accent-300 !bg-neutral-100 dark:!bg-neutral-800 font-semibold',
    page: 'bg-white dark:bg-neutral-900',
    pageScrollBox: 'bg-white dark:bg-neutral-900',
    profilePage: 'bg-white dark:bg-neutral-900',
    profileSection__profile: 'bg-white dark:bg-neutral-900',
    profileSection__security: 'bg-white dark:bg-neutral-900',
    profileSection__general: 'bg-white dark:bg-neutral-900',
    profileSection__danger: 'bg-white dark:bg-neutral-900',
    profileSectionPrimaryButton:
      'bg-action hover:bg-action-hover text-action-foreground font-medium py-2 px-4 rounded-lg',
    formButtonPrimary:
      'bg-action hover:bg-action-hover text-action-foreground font-medium py-2 px-4 rounded-lg',
    formButtonReset:
      'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100',
    formFieldInput:
      'form-field focus:ring-0 bg-white dark:bg-neutral-950 border-neutral-300 dark:border-neutral-700/50 text-neutral-900 dark:text-neutral-100',
    formFieldLabel: 'text-neutral-700 dark:text-neutral-300 font-medium',
    headerTitle: 'text-neutral-900 dark:text-neutral-100',
    headerSubtitle: 'text-neutral-600 dark:text-neutral-400',
    accordionTriggerButton: 'text-neutral-700 dark:text-neutral-300',
    badge:
      'bg-accent-5 text-accent-700 dark:bg-accent-700/30 dark:text-accent-300',
    avatarBox:
      'w-24 h-24 rounded-full bg-neutral-100 dark:bg-neutral-800 border-4 border-white dark:border-neutral-900',
  },
};

// Aliases for backwards compatibility
export const userProfileAppearance = profileAppearance;
